import React, { useEffect, useState, useCallback } from "react";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

/** Vendor-Regeln verwalten: Substring-Pattern → Konto. */
export function VendorRulesManager({ companyId }) {
  const [rules, setRules] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newPattern, setNewPattern] = useState("");
  const [newAccountId, setNewAccountId] = useState("");
  const [newNote, setNewNote] = useState("");

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [r, a] = await Promise.all([
        apiClient.get(`/companies/${companyId}/vendor-rules`),
        apiClient.get(`/companies/${companyId}/accounts`),
      ]);
      setRules(r.data || []);
      setAccounts(a.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const submitNew = async () => {
    if (!newPattern.trim() || !newAccountId) {
      toast.error("Pattern und Konto sind Pflicht.");
      return;
    }
    setSaving(true);
    try {
      await apiClient.post(`/companies/${companyId}/vendor-rules`, {
        vendor_pattern: newPattern.trim(),
        account_id: newAccountId,
        note: newNote.trim() || null,
      });
      toast.success(`Regel gespeichert: "${newPattern}" → ${accountLabel(newAccountId)}`);
      setNewPattern("");
      setNewAccountId("");
      setNewNote("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const deleteRule = async (rule) => {
    if (!window.confirm(`Regel "${rule.vendor_pattern}" wirklich löschen?`)) return;
    try {
      await apiClient.delete(`/vendor-rules/${rule._id}`);
      toast.success("Regel gelöscht.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen.");
    }
  };

  const accountLabel = (id) => {
    const a = accounts.find((x) => x._id === id);
    return a ? `${a.code} ${a.name}` : id;
  };

  return (
    <div className="space-y-4" data-testid="vendor-rules-manager">
      <div className="text-xs text-slate-500 leading-relaxed">
        Wenn ein hochgeladener Beleg den unten festgelegten Text enthält (z.B. "BP Tankstelle" im Beschreibungstext oder Vendor-Namen), wird das verknüpfte Konto automatisch vorgewählt — der Buchhalter muss nur noch bestätigen.
      </div>

      {/* Add-Formular */}
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
        <div className="text-xs font-semibold text-slate-700 mb-1">Neue Regel anlegen</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div>
            <Label htmlFor="vrule-pattern" className="text-[11px] text-slate-600">
              Vendor / Beleg-Text (Substring)
            </Label>
            <Input
              id="vrule-pattern"
              value={newPattern}
              onChange={(e) => setNewPattern(e.target.value)}
              placeholder="z.B. BP Tankstelle"
              className="h-8 text-xs"
              data-testid="vendor-rules-new-pattern"
            />
          </div>
          <div>
            <Label htmlFor="vrule-account" className="text-[11px] text-slate-600">
              Konto
            </Label>
            <Select value={newAccountId} onValueChange={setNewAccountId}>
              <SelectTrigger id="vrule-account" className="h-8 text-xs" data-testid="vendor-rules-new-account">
                <SelectValue placeholder="Konto wählen ..." />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {accounts.map((a) => (
                  <SelectItem key={a._id} value={a._id}>
                    {a.code} {a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label htmlFor="vrule-note" className="text-[11px] text-slate-600">
            Notiz (optional)
          </Label>
          <Input
            id="vrule-note"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="z.B. Treibstoff für Geschäftsfahrzeug"
            className="h-8 text-xs"
            data-testid="vendor-rules-new-note"
          />
        </div>
        <div className="flex justify-end pt-1">
          <Button
            type="button"
            size="sm"
            onClick={submitNew}
            disabled={saving || !newPattern.trim() || !newAccountId}
            className="rounded-full bg-slate-900 text-white"
            data-testid="vendor-rules-save"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Plus className="h-3.5 w-3.5 mr-1.5" />}
            Regel hinzufügen
          </Button>
        </div>
      </div>

      {/* Existing rules list */}
      <div>
        <div className="text-xs font-semibold text-slate-700 mb-2">
          Bestehende Regeln ({rules.length})
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-xs text-slate-500 py-3">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Lade ...
          </div>
        ) : rules.length === 0 ? (
          <div className="text-xs text-slate-500 italic py-3 text-center border border-dashed border-slate-200 rounded">
            Noch keine Regeln. Lege oben deine erste an oder klicke beim Beleg-Buchen auf "Regel speichern".
          </div>
        ) : (
          <div className="space-y-1">
            {rules.map((r) => (
              <div
                key={r._id}
                className="flex items-center gap-3 px-3 py-2 rounded-md border border-slate-200 bg-white"
                data-testid={`vendor-rule-${r._id}`}
              >
                <Sparkles className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs flex-wrap">
                    <span className="font-mono font-medium text-slate-900 truncate">
                      "{r.vendor_pattern}"
                    </span>
                    <span className="text-slate-400">→</span>
                    <span className="font-medium text-slate-700">{accountLabel(r.account_id)}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {r.applies_count > 0 ? (
                      <>{r.applies_count}× angewendet</>
                    ) : (
                      <>Noch nicht angewendet</>
                    )}
                    {r.note ? ` · ${r.note}` : ""}
                  </div>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0 text-rose-600 hover:bg-rose-50"
                  onClick={() => deleteRule(r)}
                  data-testid={`vendor-rule-delete-${r._id}`}
                  title="Regel löschen"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default VendorRulesManager;
